import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, User, Phone, MessageSquare, RefreshCw } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { nome: "", cognome: "", telefono: "", note: "" };

export default function InviaOspiteReteDialog({ open, onOpenChange }) {
  const { token, user, activeStruttura } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const strutturaUtente =
    (activeStruttura?.name && String(activeStruttura.name).trim()) ||
    (user?.facility_name && String(user.facility_name).trim()) ||
    "Struttura";
  const strutturaId =
    activeStruttura?.id ||
    user?.struttura_id ||
    user?.activeStrutturaId ||
    activeStruttura?.zohoRecordId ||
    user?.zohoRecordId;

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleClose = (v) => {
    if (!v) setForm(EMPTY);
    onOpenChange(v);
  };

  const handleSubmit = async () => {
    if (!form.nome.trim() || !form.cognome.trim() || !form.telefono.trim()) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ospiti-in-rete`,
        {
          nome: form.nome.trim(),
          cognome: form.cognome.trim(),
          telefono: form.telefono.trim(),
          note: form.note.trim(),
          struttura_id: strutturaId,
          struttura_nome: strutturaUtente,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success("Ospite inviato con successo!");
        handleClose(false);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Errore nell'invio dell'ospite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Send className="w-6 h-6 text-blue-500" />
            Invia Ospite in Rete
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Invia un ospite a Casadiriposoperte.it
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              Nome *
            </Label>
            <Input value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Inserisci il nome" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4 text-slate-500" />
              Cognome *
            </Label>
            <Input value={form.cognome} onChange={(e) => set("cognome", e.target.value)} placeholder="Inserisci il cognome" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-500" />
              Telefono *
            </Label>
            <Input type="tel" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="Inserisci il numero di telefono" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-500" />
              Note
            </Label>
            <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Inserisci eventuali note..." rows={3} />
          </div>
          <div className="p-3 bg-blue-50 rounded-lg text-sm">
            <p className="text-blue-700">
              <strong>Struttura segnalante:</strong> {strutturaUtente}
            </p>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => handleClose(false)} disabled={loading}>Annulla</Button>
          <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700" disabled={loading}>
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Invia Ospite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
